import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [report, setReport] = useState(null);
  const [speseFiles, setSpeseFiles] = useState([]);
  const [materialeGuastoFiles, setMaterialeGuastoFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/${id}`, {
        headers: getAuthHeader(),
        params: { t: new Date().getTime() }
      });
      setReport(response.data);
      
      // Fetch spese files
      if (response.data.spese_file_ids && response.data.spese_file_ids.length > 0) {
        const filesPromises = response.data.spese_file_ids.map(fileId =>
          axios.get(`${API}/files/${fileId}`, { headers: getAuthHeader() }).catch(() => null)
        );
        const filesResponses = await Promise.all(filesPromises);
        setSpeseFiles(filesResponses.filter(r => r !== null).map(r => r.data));
      }

      // Fetch materiale guasto files
      if (response.data.materiale_guasto_file_ids && response.data.materiale_guasto_file_ids.length > 0) {
        const filesPromises = response.data.materiale_guasto_file_ids.map(fileId =>
          axios.get(`${API}/files/${fileId}`, { headers: getAuthHeader() }).catch(() => null)
        );
        const filesResponses = await Promise.all(filesPromises);
        setMaterialeGuastoFiles(filesResponses.filter(r => r !== null).map(r => r.data));
      }
    } catch (error) {
      console.error('Errore caricamento report:', error);
      alert('Errore durante il caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const element = printRef.current;
      const opt = {
        margin: [8, 8, 8, 8],
        filename: `Report_${report.titolo_evento}_${report.data_evento}.pdf`,
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Errore esportazione PDF:', error);
      alert('Errore durante l\'esportazione del PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Report non trovato</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="report-detail-page">
      {/* Navbar - hidden in print */}
      <nav className="bg-white border-b border-gray-200 no-print">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center space-x-3">
              <h1 className="text-base font-semibold text-gray-900">BETTI SRL</h1>
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-gray-500">Dettaglio Report</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                data-testid="export-pdf-btn"
              >
                {exporting ? 'Esportazione...' : 'Esporta PDF'}
              </button>
              <button
                onClick={() => window.print()}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900"
                data-testid="print-report-btn"
              >
                Stampa
              </button>
              {user?.role !== 'visualizzatore' && (
                <button
                  onClick={() => navigate(`/modifica-report/${id}`)}
                  className="px-4 py-2 text-sm bg-gray-900 text-white hover:bg-gray-800"
                  data-testid="edit-report-btn"
                >
                  Modifica
                </button>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900"
                data-testid="back-dashboard-btn"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8" ref={printRef}>
        {/* Header */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <div className="print-header hidden">
            <h1 className="text-lg font-bold text-center mb-1">BETTI SRL - AUDIO LIGHTS VIDEO</h1>
            <p className="text-sm text-center mb-3 text-gray-600">Report Evento</p>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            <span className="text-gray-400 font-normal text-lg">Report #{report.numero_report || '—'}</span>
            {' — '}
            {report.titolo_evento}
          </h2>
          <p className="text-sm text-gray-600">{formatDate(report.data_evento)}</p>
        </div>

        {/* Veicoli */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Veicoli Usati</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">Veicoli selezionati:</p>
              <div className="flex flex-wrap gap-2">
                {report.veicoli_usati.map((v, i) => (
                  <span key={i} className="px-3 py-1 bg-gray-100 text-gray-900 text-sm border border-gray-200">
                    {v}
                  </span>
                ))}
                {report.veicolo_altro && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-900 text-sm border border-gray-200">
                    {report.veicolo_altro}
                  </span>
                )}
              </div>
            </div>
            {report.rimborsi_veicoli && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Rimborsi:</p>
                <p className="text-sm text-gray-900">{report.rimborsi_veicoli}</p>
              </div>
            )}
            {report.danni_veicoli && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Danni/Problemi:</p>
                <p className="text-sm text-gray-900">{report.danni_veicoli}</p>
              </div>
            )}
          </div>
        </div>

        {/* Personale */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Personale su Evento</h3>
          <div className="space-y-4">
            {report.turni_personale && report.turni_personale.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-3">Turni:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {report.turni_personale.map((turno, i) => (
                    <div key={i} className="flex justify-between items-center px-3 py-2 bg-gray-50 border border-gray-200">
                      <span className="text-sm text-gray-900">{turno.nome}</span>
                      <span className="text-sm font-medium text-gray-900">{turno.ore} turni</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {report.altri_personale && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Altri:</p>
                <p className="text-sm text-gray-900">{report.altri_personale}</p>
              </div>
            )}
            {report.extratime_ore > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Extratime:</p>
                <p className="text-sm text-gray-900">{report.extratime_ore} turni</p>
              </div>
            )}
            {report.materiale_extra && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Materiale Extra:</p>
                <p className="text-sm text-gray-900">{report.materiale_extra}</p>
              </div>
            )}
          </div>
        </div>

        {/* Orari */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Orari</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Partenza Magazzino</p>
              <p className="text-sm font-medium text-gray-900">{report.orario_partenza}</p>
            </div>
            {report.orario_arrivo && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Arrivo Location</p>
                <p className="text-sm font-medium text-gray-900">{report.orario_arrivo}</p>
              </div>
            )}
            {report.orario_inizio_evento && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Inizio Evento</p>
                <p className="text-sm font-medium text-gray-900">{report.orario_inizio_evento}</p>
              </div>
            )}
            {report.orario_fine_evento && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Fine Evento</p>
                <p className="text-sm font-medium text-gray-900">{report.orario_fine_evento}</p>
              </div>
            )}
            {report.orario_fine_smontaggio && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Fine Smontaggio</p>
                <p className="text-sm font-medium text-gray-900">{report.orario_fine_smontaggio}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-1">Rientro</p>
              <p className="text-sm font-medium text-gray-900">{report.orario_rientro}</p>
            </div>
          </div>
        </div>

        {/* Spese */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Spese</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">Riepilogo:</p>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{report.riepilogo_spese}</p>
            </div>
            {speseFiles.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Foto scontrini/spese:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print-images">
                  {speseFiles.map((file) => (
                    <div key={file.id} className="border border-gray-200 overflow-hidden cursor-pointer hover:border-gray-400 transition">
                      <img
                        src={`data:${file.content_type};base64,${file.data}`}
                        alt={file.filename}
                        className="w-full h-32 object-cover print-img"
                        onClick={() => window.open(`data:${file.content_type};base64,${file.data}`, '_blank')}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {report.spese_altro && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Altro:</p>
                <p className="text-sm text-gray-900">{report.spese_altro}</p>
              </div>
            )}
          </div>
        </div>

        {/* Problemi e Segnalazioni */}
        {(report.materiale_guasto || report.problemi_cliente || report.problemi_personale || materialeGuastoFiles.length > 0) && (
          <div className="bg-white border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Segnalazioni</h3>
            <div className="space-y-4">
              {report.materiale_guasto && (
                <div>
                  <p className="text-sm font-medium text-red-600 mb-1">Materiale Guasto:</p>
                  <p className="text-sm text-gray-900">{report.materiale_guasto}</p>
                </div>
              )}
              {materialeGuastoFiles.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Foto materiale guasto:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print-images">
                    {materialeGuastoFiles.map((file) => (
                      <div key={file.id} className="border border-orange-200 overflow-hidden cursor-pointer hover:border-orange-400 transition">
                        <img
                          src={`data:${file.content_type};base64,${file.data}`}
                          alt={file.filename}
                          className="w-full h-32 object-cover print-img"
                          onClick={() => window.open(`data:${file.content_type};base64,${file.data}`, '_blank')}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {report.problemi_cliente && (
                <div>
                  <p className="text-sm font-medium text-red-600 mb-1">Problemi Cliente/Location:</p>
                  <p className="text-sm text-gray-900">{report.problemi_cliente}</p>
                </div>
              )}
              {report.problemi_personale && (
                <div>
                  <p className="text-sm font-medium text-red-600 mb-1">Problemi Personale:</p>
                  <p className="text-sm text-gray-900">{report.problemi_personale}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDetail;
